/**
 * Setup Actions Utility
 *
 * Determines which recommended setup actions to show to users
 * based on their product focus and current configuration status.
 */

import {ProductFocus} from "@/constants/onboarding";
import {AgencyVerificationStatus} from "@/enums/agencies";
import {IntegrationTab, integrationTabRoute} from "@/types/integrations";
import {Org} from "@/types/org";
import {ConfigurationStatus} from "@/utils/onboarding/configDetection";

// Setup action priorities (lower = higher priority)
export enum SetupActionPriority {
   CRITICAL = 1,
   HIGH = 2,
   MEDIUM = 3,
   LOW = 4,
}

// Setup action types
export type SetupActionType =
   | "voip_setup"
   | "number_masking"
   | "business_verification"
   | "voice_customization"
   | "knowledge_base"
   | "test_conversation"
   | "integrations"
   | "calendar_integration"
   | "additional_faq"
   | "sms_approval"
   | "data_extraction";

export interface SetupAction {
   type: SetupActionType;
   titleKey: string;
   descriptionKey: string;
   priority: SetupActionPriority;
   products: ProductFocus[]; // Which products this action applies to
   route?: string; // Optional route to navigate to
   externalUrl?: string; // Optional external URL
   isCompleted?: boolean;
   isInProgress?: boolean;
}

// Define all available setup actions
const ALL_SETUP_ACTIONS: SetupAction[] = [
   // Inbound-specific actions
   {
      type: "voip_setup",
      titleKey: "setupActions.voipSetup.title",
      descriptionKey: "setupActions.voipSetup.description",
      priority: SetupActionPriority.HIGH,
      products: ["inbound"],
      route: "/settings/general",
   },
   {
      type: "calendar_integration",
      titleKey: "setupActions.calendarIntegration.title",
      descriptionKey: "setupActions.calendarIntegration.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["inbound"],
      route: integrationTabRoute(IntegrationTab.Calendar),
   },
   {
      type: "additional_faq",
      titleKey: "setupActions.additionalFaq.title",
      descriptionKey: "setupActions.additionalFaq.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["inbound"],
      route: "/knowledge/faqs",
   },

   // Outbound-specific actions
   {
      type: "number_masking",
      titleKey: "setupActions.numberMasking.title",
      descriptionKey: "setupActions.numberMasking.description",
      priority: SetupActionPriority.HIGH,
      products: ["outbound"],
      route: "/settings/general",
   },
   {
      type: "business_verification",
      titleKey: "setupActions.businessVerification.title",
      descriptionKey: "setupActions.businessVerification.description",
      priority: SetupActionPriority.HIGH,
      products: ["outbound"],
      route: "/settings/general",
   },
   {
      type: "sms_approval",
      titleKey: "setupActions.smsApproval.title",
      descriptionKey: "setupActions.smsApproval.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["outbound"],
      route: "/settings/general",
   },

   // GailGPT-specific actions
   {
      type: "knowledge_base",
      titleKey: "setupActions.knowledgeBase.title",
      descriptionKey: "setupActions.knowledgeBase.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["gailgpt"],
      route: "/assistants",
   },
   {
      type: "test_conversation",
      titleKey: "setupActions.testConversation.title",
      descriptionKey: "setupActions.testConversation.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["gailgpt"],
      route: "/assistants",
   },

   // Shared actions
   {
      type: "voice_customization",
      titleKey: "setupActions.voiceCustomization.title",
      descriptionKey: "setupActions.voiceCustomization.description",
      priority: SetupActionPriority.LOW,
      products: ["inbound", "outbound"],
      route: "/settings/voices",
   },
   {
      type: "integrations",
      titleKey: "setupActions.integrations.title",
      descriptionKey: "setupActions.integrations.description",
      priority: SetupActionPriority.MEDIUM,
      products: ["inbound", "outbound", "gailgpt"],
      route: integrationTabRoute(IntegrationTab.Apps),
   },
   {
      type: "data_extraction",
      titleKey: "setupActions.dataExtraction.title",
      descriptionKey: "setupActions.dataExtraction.description",
      priority: SetupActionPriority.LOW,
      products: ["inbound", "outbound"],
      route: "/knowledge/dataExtraction",
   },
];

/**
 * Check if a specific setup action is completed based on org configuration
 */
function isActionCompleted(
   action: SetupAction,
   org: Org,
   configStatus: ConfigurationStatus
): boolean {
   switch (action.type) {
      case "voip_setup":
         // Check if a phone number is configured
         return Boolean(org.services?.twilioPhoneNumber);

      case "number_masking":
         // A verified caller ID is what enables outbound number masking
         return Boolean(org.services?.twilioVerifiedPhoneNumber);

      case "business_verification":
         // Same signal the settings page uses for agency verification
         return org.details?.agencyVerificationStatus === AgencyVerificationStatus.Verified;

      case "knowledge_base":
         // Check if assistant has knowledge base
         return configStatus.hasAssistant;

      case "test_conversation":
         // Check if there have been any conversations
         return configStatus.hasConversations;

      case "voice_customization":
         // Check if any custom voice exists
         return configStatus.hasCustomVoice;

      case "integrations":
         // Check if any integrations are connected
         return configStatus.hasConnectedIntegration;

      case "calendar_integration":
         // Check if calendar is connected
         return configStatus.hasConnectedCalendar;

      case "additional_faq":
         // FAQs are set up during onboarding, recommend adding more
         return configStatus.hasFaqs;

      case "sms_approval":
         // Check if SMS campaign is approved
         return configStatus.hasApprovedSmsCampaign;

      case "data_extraction":
         // Check if data extraction fields are configured
         return Boolean(org.dataExtractionDataFields && org.dataExtractionDataFields.length > 0);

      default:
         return false;
   }
}

/**
 * Get recommended setup actions for a user based on their product focus
 */
export function getRecommendedSetupActions(
   org: Org,
   productFocus: ProductFocus[],
   configStatus: ConfigurationStatus,
   options: {
      maxActions?: number;
      includeCompleted?: boolean;
      priorityThreshold?: SetupActionPriority;
   } = {}
): SetupAction[] {
   const {
      maxActions = 5,
      includeCompleted = false,
      priorityThreshold = SetupActionPriority.LOW,
   } = options;

   // Filter actions by product focus
   let relevantActions = ALL_SETUP_ACTIONS.filter((action) =>
      action.products.some((product) => productFocus.includes(product))
   );

   // Add completion / in-progress status
   relevantActions = relevantActions.map((action) => ({
      ...action,
      isCompleted: isActionCompleted(action, org, configStatus),
      isInProgress:
         action.type === "business_verification" &&
         org.details?.agencyVerificationStatus === AgencyVerificationStatus.Pending,
   }));

   // Filter out completed actions if not including them
   if (!includeCompleted) {
      relevantActions = relevantActions.filter((action) => !action.isCompleted);
   }

   // Filter by priority threshold
   relevantActions = relevantActions.filter(
      (action) => action.priority <= priorityThreshold
   );

   // Sort by priority (ascending - lower number = higher priority)
   relevantActions.sort((a, b) => a.priority - b.priority);

   // Limit to max actions
   return relevantActions.slice(0, maxActions);
}

/**
 * Get all setup actions for a given product
 */
export function getSetupActionsForProduct(product: ProductFocus): SetupAction[] {
   return ALL_SETUP_ACTIONS.filter((action) => action.products.includes(product));
}

/**
 * Get the next most important setup action
 */
export function getNextSetupAction(
   org: Org,
   productFocus: ProductFocus[],
   configStatus: ConfigurationStatus
): SetupAction | null {
   const actions = getRecommendedSetupActions(org, productFocus, configStatus, {
      maxActions: 1,
      includeCompleted: false,
   });

   return actions[0] ?? null;
}
